import * as React from "react"
import { Check } from "lucide-react"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command"

interface CreatableComboboxProps {
  value: string
  onChange: (value: string) => void
  onConfirm: () => void
  onCancel: () => void
  options: string[]
  placeholder?: string
}

export function CreatableCombobox({
  value,
  onChange,
  onConfirm,
  onCancel,
  options,
  placeholder = "Select or type...",
}: CreatableComboboxProps) {
  const [open, setOpen] = React.useState(true)
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      onConfirm()
    } else if (e.key === "Escape") {
      e.preventDefault()
      onCancel()
    }
  }

  const handleSelectOption = (selectedValue: string) => {
    onChange(selectedValue)
    setOpen(false)
    // Delay to allow state update before confirming
    setTimeout(() => onConfirm(), 0)
  }

  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(value.toLowerCase())
  )

  const showCreateNew = value && !options.some((opt) => opt.toLowerCase() === value.toLowerCase())

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Input
            ref={inputRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="h-8 px-2 py-1"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandList>
            <CommandEmpty>
              {showCreateNew ? (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  Press Enter to create "{value}"
                </div>
              ) : (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  No options found
                </div>
              )}
            </CommandEmpty>
            {filteredOptions.length > 0 && (
              <CommandGroup>
                {filteredOptions.map((option) => (
                  <CommandItem
                    key={option}
                    value={option}
                    onSelect={() => handleSelectOption(option)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value.toLowerCase() === option.toLowerCase()
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                    {option}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
